import React, { useState, useEffect } from "react";
import Footer from "../../components/Footer/Footer";

const ThankYou: React.FC = () => {
    // Theme state: defaults to 'dark'
    const [theme, setTheme] = useState<"dark" | "light">(() => {
        if (typeof window !== "undefined") {
            const savedTheme = localStorage.getItem("theme");
            return (savedTheme as "dark" | "light") || "dark";
        }
        return "dark";
    });

    // Apply theme class to document
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(theme);
        localStorage.setItem("theme", theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme((prev) => (prev === "dark" ? "light" : "dark"));
    };

    useEffect(() => {
        // Fire Facebook Pixel standard event on page load for CompleteRegistration
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            window.fbq("track", "CompleteRegistration");
        }
    }, []);

    return (
        <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 min-h-screen flex flex-col font-display selection:bg-primary selection:text-white transition-colors duration-300">
            {/* Header / TopAppBar */}
            <header className="w-full max-w-4xl mx-auto px-4 py-6 flex items-center justify-between border-b border-gray-200 dark:border-white/10">
                <div className="flex items-center w-full relative justify-between">
                    <div className="flex items-center gap-2">
                        <div className="bg-primary p-1.5 rounded-lg">
                            <span className="material-symbols-outlined text-white text-2xl">fitness_center</span>
                        </div>
                        <span className="text-xl font-bold tracking-tight text-slate-900 dark:text-white uppercase hidden sm:block">Chibs Olympia</span>
                    </div>

                    <span className="text-xl font-bold tracking-tight text-slate-900 dark:text-white uppercase sm:hidden absolute left-1/2 -translate-x-1/2 truncate max-w-[50%] text-center">Chibs Olympia</span>

                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors z-10 relative"
                        aria-label="Toggle theme"
                    >
                        <span className="material-symbols-outlined text-slate-600 dark:text-gray-300">
                            {theme === "dark" ? "light_mode" : "dark_mode"}
                        </span>
                    </button>
                </div>
            </header>

            <main className="flex-1 w-full max-w-3xl mx-auto px-6 py-12 md:py-20 text-center">
                {/* Hero Section */}
                <div className="mb-12">
                    <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white mb-4 tracking-tight uppercase">
                        THANK <span className="text-primary">YOU</span>
                    </h1>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-6">You've successfully booked your strategy call.</h2>
                    <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                        This is where things get serious. Watch the video below to understand what happens next.
                    </p>


                </div>

                {/* Before Our Call */}
                <div className="mb-12">
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-8 text-center uppercase tracking-widest">
                        BEFORE <span className="border-b-4 border-primary pb-1">OUR</span> CALL
                    </h3>

                    <div className="grid grid-cols-1 gap-6 text-left">
                        {/* Step 1 */}
                        <div className="bg-white dark:bg-[#151a28] p-6 md:p-8 rounded-2xl border border-gray-200 dark:border-white/5 hover:border-primary/30 transition-colors shadow-sm">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                                <span className="material-symbols-outlined text-primary text-2xl">chat</span>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">1. Check WhatsApp</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">I'll message you personally with your specific preparation instructions for the call.</p>
                        </div>

                        {/* Step 2 */}
                        <div className="bg-white dark:bg-[#151a28] p-6 md:p-8 rounded-2xl border border-gray-200 dark:border-white/5 hover:border-primary/30 transition-colors shadow-sm">
                            <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-6 bg-gray-100 dark:bg-slate-800">
                                <iframe
                                    src="https://www.youtube.com/embed/soHJ3dskHSY"
                                    title="The TMAN Method"
                                    className="absolute top-0 left-0 w-full h-full border-0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                ></iframe>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">2. Watch The TMAN Method</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">This video explains the exact framework we use to build elite-level physiques sustainably.</p>
                        </div>

                        {/* Step 3 */}
                        <div className="bg-white dark:bg-[#151a28] p-6 md:p-8 rounded-2xl border border-gray-200 dark:border-white/5 hover:border-primary/30 transition-colors shadow-sm">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                                <span className="material-symbols-outlined text-primary text-2xl">assignment</span>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">3. Send Your 4 Scores</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">Rate yourself 1-10 in Training, Mindset, Accountability, and Nutrition so we can hit the ground running.</p>
                        </div>
                    </div>
                </div>

                {/* Highlight Box */}
                <section className="mb-12" data-purpose="critical-warning">
                    <div className="bg-primary rounded-2xl p-8 text-center text-white border-l-8 border-white shadow-lg overflow-hidden relative">
                        <p className="text-lg md:text-xl font-bold italic relative z-10 flex items-start sm:items-center text-left sm:text-center flex-col sm:flex-row gap-4 justify-center">
                            <span className="inline-flex items-center justify-center bg-white text-primary w-8 h-8 rounded shrink-0 not-italic">!</span>
                            <span>Important: "If we don't receive your scores before the call, we won't be able to go deep."</span>
                        </p>
                    </div>
                </section>

                {/* Call Expectations */}
                <section className="mb-16" data-purpose="expectations-checklist">
                    <div className="bg-gray-100 dark:bg-[#151a28]/80 p-8 md:p-12 rounded-3xl border border-gray-200 dark:border-white/10 text-left w-full max-w-2xl mx-auto">
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-8 text-center uppercase tracking-widest">CALL EXPECTATIONS</h3>
                        <ul className="space-y-6">
                            <li className="flex items-start gap-4">
                                <div className="mt-1 shrink-0">
                                    <span className="material-symbols-outlined text-primary font-bold">check</span>
                                </div>
                                <span className="text-lg text-slate-700 dark:text-gray-200">Be in a quiet environment where you can focus.</span>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="mt-1 shrink-0">
                                    <span className="material-symbols-outlined text-primary font-bold">check</span>
                                </div>
                                <span className="text-lg text-slate-700 dark:text-gray-200">Be on time. We respect each other's schedules.</span>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="mt-1 shrink-0">
                                    <span className="material-symbols-outlined text-primary font-bold">check</span>
                                </div>
                                <span className="text-lg text-slate-700 dark:text-gray-200">Block out 45–60 minutes of uninterrupted time.</span>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="mt-1 shrink-0">
                                    <span className="material-symbols-outlined text-primary font-bold">check</span>
                                </div>
                                <span className="text-lg text-slate-900 dark:text-white font-bold">Come ready to make a decision if it's a fit.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Quote Footer */}
                <div className="mb-12">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white italic text-center uppercase leading-tight">
                        "IF WE DECIDE TO<br />MOVE FORWARD, WE<br />MOVE FORWARD<br />PROPERLY."
                    </h2>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ThankYou;
